import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../helpers/base_url";

const EditProfile: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    address: "",
    city: "",
    state: "",
    country: "Nigeria",
    zipCode: "",
    companyName: "",
    profileImageUrl: "",
  });

  // Load user data on mount
  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("user") || "null");
      if (!parsed) {
        navigate("/login");
        return;
      }
      setUser(parsed);
      // Populate form with existing user data
      setFormData({
        firstName: parsed.userFirstName || parsed.firstName || "",
        lastName: parsed.userLastName || parsed.lastName || "",
        phoneNumber: parsed.phoneNumber || "",
        address: parsed.address || "",
        city: parsed.city || "",
        state: parsed.state || "",
        country: parsed.country || "Nigeria",
        zipCode: parsed.zipCode || "",
        companyName: parsed.companyName || "",
        profileImageUrl: parsed.profileImageUrl || "",
      });
      if (parsed.profileImageUrl) {
        setImagePreview(parsed.profileImageUrl);
      }
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setMessage({
        type: "error",
        text: "Please select an image file.",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({
        type: "error",
        text: "Image size must be less than 5MB.",
      });
      return;
    }

    // Show local preview immediately
    const localPreview = URL.createObjectURL(file);
    setImagePreview(localPreview);

    setUploadingImage(true);
    setMessage({ type: "", text: "" });

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const response = await fetch(buildApiUrl("/file/"), {
        method: "POST",
        body: formDataUpload,
      });

      const data = await response.json();

      if (response.ok && data.success && data.data) {
        // Revoke local preview URL
        URL.revokeObjectURL(localPreview);
        // Update form with the image URL
        setFormData({
          ...formData,
          profileImageUrl: data.data,
        });
        // Set preview to uploaded URL
        setImagePreview(data.data);
        setMessage({
          type: "success",
          text: "Image uploaded successfully!",
        });
      } else {
        // Revoke local preview on error
        URL.revokeObjectURL(localPreview);
        setImagePreview(formData.profileImageUrl || null);
        setMessage({
          type: "error",
          text: data.message || "Failed to upload image. Please try again.",
        });
      }
    } catch (error) {
      // Revoke local preview on error
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(formData.profileImageUrl || null);
      setMessage({
        type: "error",
        text: "Network error. Please check your connection.",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Not authenticated");
      }

      if (!user?.actorId && !user?.id) {
        throw new Error("User ID not found");
      }

      const clientId = user.actorId || user.id;
      const response = await fetch(buildApiUrl(`/api/clients/${clientId}`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update user in localStorage
        const updatedUser = {
          ...user,
          userFirstName: formData.firstName,
          firstName: formData.firstName,
          userLastName: formData.lastName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          zipCode: formData.zipCode,
          companyName: formData.companyName,
          profileImageUrl: formData.profileImageUrl,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        window.dispatchEvent(new Event("localStorageChange"));

        setMessage({
          type: "success",
          text: "Profile updated successfully!",
        });

        // Redirect to profile page after 1.5 seconds
        setTimeout(() => {
          navigate("/profile");
        }, 1500);
      } else {
        setMessage({
          type: "error",
          text: data.message || "Failed to update profile. Please try again.",
        });
      }
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error.message || "Network error. Please check your connection.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#dc3545",
          fontSize: 22,
        }}
      >
        Loading...
      </div>
    );
  }

  return (
    <div
      style={{
        background: "linear-gradient(120deg, #f8f8ff 0%, #e0e7ff 100%)",
        minHeight: "100vh",
        padding: "48px 0",
      }}
    >
      <div
        style={{
          maxWidth: 600,
          margin: "0 auto",
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 4px 24px #eee",
          padding: 40,
        }}
      >
        <h2
          style={{
            color: "#6C63FF",
            fontWeight: 800,
            fontSize: 32,
            marginBottom: 24,
            textAlign: "center",
          }}
        >
          Edit Profile
        </h2>

        {/* Message Display */}
        {message.text && (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: "8px",
              marginBottom: "20px",
              backgroundColor:
                message.type === "success" ? "#d4edda" : "#f8d7da",
              color: message.type === "success" ? "#155724" : "#721c24",
              border: `1px solid ${
                message.type === "success" ? "#c3e6cb" : "#f5c6cb"
              }`,
            }}
          >
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Profile Image Upload */}
          <div style={{ marginBottom: 24, textAlign: "center" }}>
            <label
              style={{
                display: "block",
                marginBottom: 12,
                fontWeight: 600,
                color: "#222",
              }}
            >
              Profile Image
            </label>
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Profile preview"
                style={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "4px solid #6C63FF22",
                  marginBottom: 12,
                }}
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploadingImage}
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
            {uploadingImage && (
              <div
                style={{
                  marginTop: "8px",
                  color: "#6C63FF",
                  fontSize: "14px",
                }}
              >
                Uploading image...
              </div>
            )}
            {imagePreview && (
              <button
                type="button"
                onClick={() => {
                  if (imagePreview && imagePreview.startsWith("blob:")) {
                    URL.revokeObjectURL(imagePreview);
                  }
                  setImagePreview(null);
                  setFormData({
                    ...formData,
                    profileImageUrl: "",
                  });
                  const fileInput = document.querySelector(
                    'input[type="file"]'
                  ) as HTMLInputElement;
                  if (fileInput) fileInput.value = "";
                }}
                style={{
                  display: "block",
                  margin: "8px auto 0",
                  padding: "6px 12px",
                  background: "#dc3545",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Remove Image
              </button>
            )}
          </div>

          <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontWeight: 600,
                  color: "#222",
                }}
              >
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#333",
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontWeight: 600,
                  color: "#222",
                }}
              >
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#333",
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: "block",
                marginBottom: 6,
                fontWeight: 600,
                color: "#222",
              }}
            >
              Phone Number *
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                color: "#333",
              }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: "block",
                marginBottom: 6,
                fontWeight: 600,
                color: "#222",
              }}
            >
              Address *
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                color: "#333",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontWeight: 600,
                  color: "#222",
                }}
              >
                City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#333",
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontWeight: 600,
                  color: "#222",
                }}
              >
                State *
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#333",
                }}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontWeight: 600,
                  color: "#222",
                }}
              >
                Country *
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#333",
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontWeight: 600,
                  color: "#222",
                }}
              >
                Zip Code *
              </label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#333",
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: "block",
                marginBottom: 6,
                fontWeight: 600,
                color: "#222",
              }}
            >
              Company Name (Optional)
            </label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                color: "#333",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                flex: 1,
                background: isLoading ? "#ccc" : "#6C63FF",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "12px 24px",
                fontWeight: 700,
                fontSize: 16,
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
            >
              {isLoading ? "Updating..." : "Update Profile"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/profile")}
              style={{
                flex: 1,
                background: "#eee",
                color: "#444",
                border: "none",
                borderRadius: "8px",
                padding: "12px 24px",
                fontWeight: 600,
                fontSize: 16,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;

